import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dismissNotification } from '../../config/notifications';
import GlowIcon from '../GlowIcons';

const VARIANT_ICON_MAP = {
  info: <GlowIcon name="radar" size={24} color="#00a4ef" />,
  success: <GlowIcon name="check" size={24} color="#20c997" />,
  warning: <GlowIcon name="lightning" size={24} color="#ffc107" />,
  promo: <GlowIcon name="star" size={24} color="#ff3366" />,
  error: <GlowIcon name="shield" size={24} color="#e63950" />
};

/**
 * Minik pencere — admin: popupPosition, popupSize, backdrop, gecikme, otomatik kapanma
 */
const NotificationPopup = ({ item, settings, onClose }) => {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => {
      setOpen(false);
      if (item.dismissible) {
        dismissNotification(item.id, { sessionOnly: item.showOncePerSession });
      }
      onClose?.(item.id);
    }, 260);
  }, [item, onClose]);

  useEffect(() => {
    const delay = item.openDelayMs ?? 0;
    const t = window.setTimeout(() => setOpen(true), delay);
    return () => window.clearTimeout(t);
  }, [item.openDelayMs]);

  useEffect(() => {
    const ms = item.autoCloseMs ?? settings?.popupAutoCloseMs ?? 0;
    if (!open || !ms || ms <= 0) return undefined;
    const t = window.setTimeout(close, ms);
    return () => window.clearTimeout(t);
  }, [open, item.autoCloseMs, settings?.popupAutoCloseMs, close]);

  useEffect(() => {
    if (!open || !item.lockScroll) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, item.lockScroll]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && item.dismissible) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, item.dismissible, close]);

  if (!open) return null;

  const position = item.popupPosition || 'center';
  const size = item.popupSize || 'md';
  const blur = settings?.popupBackdropBlur !== false;
  const icon = VARIANT_ICON_MAP[item.variant] || <GlowIcon name="star" size={24} color="#ff3366" />;

  const renderLink = (text, url, className) => {
    if (!text || !url) return null;
    const external = /^https?:\/\//i.test(url);
    if (external) {
      return (
        <a
          href={url}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => close()}
        >
          {text}
        </a>
      );
    }
    return (
      <Link to={url} className={className} onClick={() => close()}>
        {text}
      </Link>
    );
  };

  return (
    <div
      className={`notify-popup-root notify-popup-root--${position} ${
        leaving ? 'notify-popup-root--leave' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`notify-popup-title-${item.id}`}
    >
      {item.showBackdrop !== false ? (
        <button
          type="button"
          className={`notify-popup-backdrop ${blur ? 'notify-popup-backdrop--blur' : ''}`}
          aria-label="Kapat"
          onClick={() => {
            if (item.backdropClose !== false && item.dismissible) close();
          }}
        />
      ) : null}

      <div
        className={`notify-popup-window notify-popup-window--${size} notify-popup-window--${item.variant} glass-card-premium spotlight-card`}
        style={{ border: '1px solid var(--color-border-glass)', borderRadius: '20px' }}
      >
        <div className="notify-popup-titlebar">
          <span className="notify-popup-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="notify-popup-titlebar-title" id={`notify-popup-title-${item.id}`}>
            {item.title || 'Bildirim'}
          </span>
          {item.dismissible ? (
            <button
              type="button"
              className="notify-popup-titlebar-close"
              onClick={close}
              aria-label="Pencereyi kapat"
            >
              ×
            </button>
          ) : (
            <span className="notify-popup-titlebar-spacer" />
          )}
        </div>

        <div className="notify-popup-body" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px' }}>
          <div className="notify-popup-icon" aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </div>
          <div className="notify-popup-content">
            {item.title && position !== 'center' ? (
              <h3 className="notify-popup-heading">{item.title}</h3>
            ) : null}
            {item.message ? <p className="notify-popup-message">{item.message}</p> : null}
          </div>
        </div>

        {(item.linkText || item.secondaryLinkText) ? (
          <div className="notify-popup-footer">
            {renderLink(item.linkText, item.linkUrl, 'notify-popup-btn notify-popup-btn--primary')}
            {item.secondaryLinkText && item.secondaryLinkUrl
              ? renderLink(
                  item.secondaryLinkText,
                  item.secondaryLinkUrl,
                  'notify-popup-btn notify-popup-btn--ghost'
                )
              : item.secondaryLinkText ? (
                  <button
                    type="button"
                    className="notify-popup-btn notify-popup-btn--ghost"
                    onClick={close}
                  >
                    {item.secondaryLinkText}
                  </button>
                ) : null}
            {!item.linkText && item.dismissible ? (
              <button type="button" className="notify-popup-btn notify-popup-btn--ghost" onClick={close}>
                Kapat
              </button>
            ) : null}
          </div>
        ) : item.dismissible ? (
          <div className="notify-popup-footer">
            <button type="button" className="notify-popup-btn notify-popup-btn--primary" onClick={close}>
              Tamam
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationPopup;
